import { useState, useEffect, useCallback } from "react";
import { adminAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Building2, Search, Plus, Copy, ExternalLink, Mail, Phone, ArrowLeft,
  Upload, RefreshCw, Target, ChevronDown, Pencil, Trash2, CheckCircle2,
  X, FileText, Calendar, MessageSquare, Eye
} from "lucide-react";
import { toast } from "sonner";

const STATES = [
  { value: "NEW", label: "New", color: "bg-slate-100 text-slate-700 border-slate-200" },
  { value: "READY_TO_CONTACT", label: "Ready", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "CONTACTED", label: "Contacted", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { value: "FOLLOW_UP", label: "Follow-up", color: "bg-purple-50 text-purple-700 border-purple-200" },
  { value: "VISITED", label: "Visited", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "REGISTERED", label: "Registered", color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  { value: "ONBOARDED", label: "Onboarded", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "JOB_PUBLISHED", label: "Job Published", color: "bg-green-50 text-green-800 border-green-300" },
  { value: "NOT_INTERESTED", label: "Not Interested", color: "bg-red-50 text-red-700 border-red-200" },
  { value: "INVALID", label: "Invalid", color: "bg-gray-50 text-gray-500 border-gray-200" },
];
const stateInfo = (val) => STATES.find(s => s.value === val) || STATES[0];
const BASE_URL = "https://staffera.eu";

function StateBadge({ status }) {
  const s = stateInfo(status);
  return <Badge className={`${s.color} text-[11px] px-2 py-0.5 font-medium`}>{s.label}</Badge>;
}

function getTrackingUrl(prospect) {
  // Use backend-computed URL if available (single source of truth)
  if (prospect.tracking_url) return prospect.tracking_url;
  // Fallback: compute client-side
  const src = prospect.source || "outreach";
  const camp = prospect.campaign || "";
  const params = new URLSearchParams();
  params.set("utm_source", src);
  params.set("utm_medium", "email");
  if (camp) params.set("utm_campaign", camp);
  params.set("utm_content", prospect.prospect_id);
  return `${BASE_URL}/for-hotels?${params.toString()}`;
}

function copyToClipboard(text, label = "Copied") {
  navigator.clipboard.writeText(text).then(() => toast.success(label)).catch(() => toast.error("Copy failed"));
}

// ── Metrics Bar ──
function MetricsBar({ metrics }) {
  if (!metrics) return null;
  const s = metrics.by_status || {};
  const c = metrics.conversions || {};
  return (
    <div className="space-y-4 mb-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {[
          ["Total", metrics.total, "text-slate-700"],
          ["New", (s.NEW || 0) + (s.READY_TO_CONTACT || 0), "text-slate-500"],
          ["Contacted", metrics.contacted, "text-indigo-600"],
          ["Visited", metrics.visited, "text-amber-600"],
          ["Registered", metrics.registered, "text-cyan-600"],
          ["Onboarded", metrics.onboarded, "text-emerald-600"],
          ["Published", metrics.published, "text-green-700"],
          ["Not interested", s.NOT_INTERESTED || 0, "text-red-500"],
        ].map(([label, val, color], i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-lg p-3 text-center">
            <p className={`font-bold text-lg ${color}`}>{val}</p>
            <p className="text-[10px] text-slate-400 font-body uppercase tracking-wider">{label}</p>
          </div>
        ))}
      </div>
      {/* Conversion rates */}
      <div className="flex flex-wrap gap-4 text-xs font-body text-slate-500">
        <span>Contacted → Visit: <strong className="text-slate-700">{c.contacted_to_visit}%</strong></span>
        <span>Visit → Register: <strong className="text-slate-700">{c.visit_to_register}%</strong></span>
        <span>Register → Onboard: <strong className="text-slate-700">{c.register_to_onboard}%</strong></span>
        <span>Onboard → Publish: <strong className="text-slate-700">{c.onboard_to_publish}%</strong></span>
      </div>
      {/* Goal progress */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-4">
        <Target className="w-5 h-5 text-gold-500 shrink-0" />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-semibold text-slate-700 font-body">Hotels with first job</span>
            <span className="text-sm font-bold text-slate-900">{metrics.goal_progress} / {metrics.goal}</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-2.5">
            <div className="bg-emerald-500 h-2.5 rounded-full transition-all" style={{ width: `${Math.min(100, (metrics.goal_progress / metrics.goal) * 100)}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Create/Edit Form ──
function ProspectForm({ prospect, onSave, onCancel }) {
  const isEdit = !!prospect?.prospect_id;
  const [form, setForm] = useState({
    hotel_name: prospect?.hotel_name || "",
    country: prospect?.country || "",
    region: prospect?.region || "",
    city: prospect?.city || "",
    website: prospect?.website || "",
    email: prospect?.email || "",
    phone: prospect?.phone || "",
    contact_name: prospect?.contact_name || "",
    contact_role: prospect?.contact_role || "",
    source: prospect?.source || "outreach",
    campaign: prospect?.campaign || "",
    notes: prospect?.notes || "",
    status: prospect?.status || "NEW",
    next_followup: prospect?.next_followup || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.hotel_name.trim()) { toast.error("Hotel name required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await adminAPI.updateProspect(prospect.prospect_id, form);
        toast.success("Prospect updated");
      } else {
        await adminAPI.createProspect(form);
        toast.success("Prospect created");
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const u = (field, val) => setForm(p => ({ ...p, [field]: val }));
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-xs font-body">Hotel name *</Label><Input value={form.hotel_name} onChange={e => u("hotel_name", e.target.value)} className="h-9 text-sm" required data-testid="prospect-hotel-name" /></div>
        <div><Label className="text-xs font-body">Country</Label><Input value={form.country} onChange={e => u("country", e.target.value)} className="h-9 text-sm" /></div>
        <div><Label className="text-xs font-body">Region</Label><Input value={form.region} onChange={e => u("region", e.target.value)} className="h-9 text-sm" /></div>
        <div><Label className="text-xs font-body">City</Label><Input value={form.city} onChange={e => u("city", e.target.value)} className="h-9 text-sm" /></div>
        <div><Label className="text-xs font-body">Website</Label><Input value={form.website} onChange={e => u("website", e.target.value)} className="h-9 text-sm" placeholder="https://" /></div>
        <div><Label className="text-xs font-body">Email</Label><Input type="email" value={form.email} onChange={e => u("email", e.target.value)} className="h-9 text-sm" data-testid="prospect-email" /></div>
        <div><Label className="text-xs font-body">Phone</Label><Input value={form.phone} onChange={e => u("phone", e.target.value)} className="h-9 text-sm" /></div>
        <div><Label className="text-xs font-body">Contact name</Label><Input value={form.contact_name} onChange={e => u("contact_name", e.target.value)} className="h-9 text-sm" /></div>
        <div><Label className="text-xs font-body">Contact role</Label><Input value={form.contact_role} onChange={e => u("contact_role", e.target.value)} className="h-9 text-sm" /></div>
        <div><Label className="text-xs font-body">Source</Label><Input value={form.source} onChange={e => u("source", e.target.value)} className="h-9 text-sm" placeholder="outreach" /></div>
        <div><Label className="text-xs font-body">Campaign</Label><Input value={form.campaign} onChange={e => u("campaign", e.target.value)} className="h-9 text-sm" placeholder="balearic_hotels" data-testid="prospect-campaign" /></div>
        {isEdit && (
          <>
            <div>
              <Label className="text-xs font-body">Status</Label>
              <Select value={form.status} onValueChange={v => u("status", v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{STATES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-body">Next follow-up</Label><Input type="date" value={form.next_followup || ""} onChange={e => u("next_followup", e.target.value)} className="h-9 text-sm" /></div>
          </>
        )}
      </div>
      <div><Label className="text-xs font-body">Notes</Label><Textarea value={form.notes} onChange={e => u("notes", e.target.value)} rows={3} className="text-sm" /></div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" className="bg-gold-500 hover:bg-gold-600 text-white" disabled={saving} data-testid="prospect-save-btn">
          {saving ? "Saving..." : isEdit ? "Update" : "Create"}
        </Button>
      </div>
    </form>
  );
}

// ── Prospect Detail View ──
function ProspectDetail({ prospectId, onBack, onRefresh }) {
  const [p, setP] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await adminAPI.getProspect(prospectId); setP(res.data); }
    catch { toast.error("Error loading prospect"); }
    finally { setLoading(false); }
  }, [prospectId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!p) return null;

  const utmUrl = getTrackingUrl(p);
  const a = p.activity || {};

  if (editing) return (
    <div>
      <Button variant="ghost" size="sm" onClick={() => setEditing(false)} className="mb-3 text-slate-500"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
      <ProspectForm prospect={p} onSave={() => { setEditing(false); load(); onRefresh(); }} onCancel={() => setEditing(false)} />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="prospect-detail">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-500"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}><Pencil className="w-3 h-3 mr-1" />Edit</Button>
          <Button variant="outline" size="sm" onClick={async () => { await adminAPI.markContacted(p.prospect_id); toast.success("Marked contacted"); load(); onRefresh(); }}>
            <MessageSquare className="w-3 h-3 mr-1" />Contacted
          </Button>
        </div>
      </div>

      <Card className="bg-white border-slate-200">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="font-sans font-bold text-lg text-slate-900">{p.hotel_name}</h2>
              <p className="text-sm text-slate-500 font-body">{[p.city, p.region, p.country].filter(Boolean).join(", ")}</p>
            </div>
            <StateBadge status={p.status} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm font-body">
            {p.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" /><span className="text-slate-700">{p.email}</span><button onClick={() => copyToClipboard(p.email, "Email copied")} className="text-slate-400 hover:text-slate-600"><Copy className="w-3 h-3" /></button></div>}
            {p.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-400" /><span className="text-slate-700">{p.phone}</span></div>}
            {p.website && <div className="flex items-center gap-2"><ExternalLink className="w-3.5 h-3.5 text-slate-400" /><a href={p.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">{p.website}</a></div>}
            {p.contact_name && <div className="text-slate-600">Contact: <span className="text-slate-800 font-medium">{p.contact_name}</span>{p.contact_role && <span className="text-slate-400"> — {p.contact_role}</span>}</div>}
            {p.source && <div className="text-slate-600">Source: <span className="text-slate-800">{p.source}</span></div>}
            {p.campaign && <div className="text-slate-600">Campaign: <span className="text-slate-800">{p.campaign}</span></div>}
            {p.next_followup && <div className="text-slate-600">Next follow-up: <span className="text-slate-800">{p.next_followup}</span></div>}
            {p.last_contacted && <div className="text-slate-600">Last contacted: <span className="text-slate-800">{new Date(p.last_contacted).toLocaleDateString()}</span></div>}
          </div>
          {p.notes && <div className="mt-3 p-3 bg-slate-50 rounded text-sm text-slate-600 font-body whitespace-pre-wrap">{p.notes}</div>}
        </CardContent>
      </Card>

      {/* UTM Link */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-sans">Staffera tracking link</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Input value={utmUrl} readOnly className="h-8 text-xs font-mono bg-slate-50 flex-1" data-testid="prospect-utm-url" />
            <Button variant="outline" size="sm" onClick={() => copyToClipboard(utmUrl, "Link copied")} data-testid="prospect-copy-link"><Copy className="w-3 h-3 mr-1" />Copy</Button>
          </div>
        </CardContent>
      </Card>

      {/* Activity */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-sans">Tracking activity</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-3 text-center">
            {[["Views", a.views, Eye], ["CTA clicks", a.cta_clicks, ChevronDown], ["Registrations", a.registrations, CheckCircle2], ["Onboardings", a.onboardings, Building2], ["Jobs", a.jobs, FileText]].map(([label, val, Icon], i) => (
              <div key={i}><Icon className="w-4 h-4 mx-auto text-slate-400 mb-1" /><p className="font-bold text-sm">{val}</p><p className="text-[10px] text-slate-400">{label}</p></div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* History */}
      {p.history && p.history.length > 0 && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-sans">History</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {[...p.history].reverse().map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-xs font-body text-slate-500">
                  <span className="text-slate-400 w-28 shrink-0">{h.at ? new Date(h.at).toLocaleDateString() : ""}</span>
                  <span>{h.action === "status_change" ? `${h.from} → ${h.to}` : h.action === "contacted" ? "Marked contacted" : h.action === "auto_promoted" ? `Auto: ${h.from} → ${h.to}` : h.action}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── CSV Import Dialog ──
function CSVImportDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(false);

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const res = await adminAPI.importCSVPreview(file);
      setPreview(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error reading CSV"); }
    finally { setLoading(false); }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      const rows = preview.rows.filter(r => !r.duplicate);
      const res = await adminAPI.confirmCSVImport(rows);
      toast.success(`Imported ${res.data.created} prospects`);
      onDone();
    } catch { toast.error("Import failed"); }
    finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-sans">Import prospects from CSV</DialogTitle></DialogHeader>
        {!preview ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 font-body">Upload a CSV file with columns: hotel_name, country, region, city, website, email, phone, contact_name, contact_role, source, notes</p>
            <Input type="file" accept=".csv" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="csv-file-input" />
            <Button onClick={handlePreview} disabled={!file || loading} className="bg-gold-500 hover:bg-gold-600 text-white" data-testid="csv-preview-btn">
              {loading ? "Loading..." : "Preview"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm font-body text-slate-700">{preview.rows.length} rows found, {preview.rows.filter(r => r.duplicate).length} duplicates, {preview.errors.length} errors</p>
            {preview.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">{preview.errors.map((e, i) => <div key={i}>Row {e.row}: {e.error}</div>)}</div>
            )}
            <div className="max-h-60 overflow-y-auto border rounded">
              <table className="w-full text-xs font-body">
                <thead className="bg-slate-50 sticky top-0"><tr><th className="p-2 text-left">Hotel</th><th className="p-2 text-left">City</th><th className="p-2 text-left">Email</th><th className="p-2 text-left">Status</th></tr></thead>
                <tbody>{preview.rows.map((r, i) => (
                  <tr key={i} className={r.duplicate ? "bg-amber-50" : ""}>
                    <td className="p-2">{r.hotel_name}</td>
                    <td className="p-2">{r.city}</td>
                    <td className="p-2">{r.email}</td>
                    <td className="p-2">{r.duplicate ? <Badge className="bg-amber-100 text-amber-700 text-[10px]">Duplicate: {r.duplicate.hotel_name}</Badge> : <Badge className="bg-emerald-50 text-emerald-700 text-[10px]">New</Badge>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => { setPreview(null); setFile(null); }}>Cancel</Button>
              <Button size="sm" className="bg-gold-500 hover:bg-gold-600 text-white" onClick={handleConfirm} disabled={importing || preview.rows.filter(r => !r.duplicate).length === 0} data-testid="csv-confirm-btn">
                {importing ? "Importing..." : `Import ${preview.rows.filter(r => !r.duplicate).length} prospects`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main Component ──
export default function ProspectionCRM() {
  const [view, setView] = useState("list"); // list | create | detail
  const [prospects, setProspects] = useState([]);
  const [total, setTotal] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showImport, setShowImport] = useState(false);
  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort: sortBy, page, limit: 50 };
      if (statusFilter) params.status = statusFilter;
      if (searchQ) params.q = searchQ;
      const [pRes, mRes] = await Promise.all([adminAPI.getProspects(params), adminAPI.getProspectMetrics()]);
      setProspects(pRes.data.prospects);
      setTotal(pRes.data.total);
      setMetrics(mRes.data);
    } catch { }
    finally { setLoading(false); }
  }, [statusFilter, searchQ, sortBy, page]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSync = async () => {
    try {
      const res = await adminAPI.syncProspectEvents();
      toast.success(`Synced: ${res.data.updated} prospects updated`);
      loadAll();
    } catch { toast.error("Sync failed"); }
  };

  if (view === "create") return (
    <div>
      <Button variant="ghost" size="sm" onClick={() => setView("list")} className="mb-3 text-slate-500"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
      <Card className="bg-white border-slate-200"><CardHeader><CardTitle className="text-sm font-sans">New prospect</CardTitle></CardHeader><CardContent>
        <ProspectForm onSave={() => { setView("list"); loadAll(); }} onCancel={() => setView("list")} />
      </CardContent></Card>
    </div>
  );

  if (view === "detail" && selectedId) return (
    <ProspectDetail prospectId={selectedId} onBack={() => { setView("list"); loadAll(); }} onRefresh={loadAll} />
  );

  return (
    <div className="space-y-4" data-testid="prospection-crm">
      <MetricsBar metrics={metrics} />

      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" className="bg-gold-500 hover:bg-gold-600 text-white" onClick={() => setView("create")} data-testid="new-prospect-btn">
          <Plus className="w-3.5 h-3.5 mr-1" />New prospect
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="import-csv-btn"><Upload className="w-3.5 h-3.5 mr-1" />Import CSV</Button>
        <Button variant="outline" size="sm" onClick={handleSync} data-testid="sync-events-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" />Sync events</Button>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search..." value={searchQ} onChange={e => { setSearchQ(e.target.value); setPage(1); }} className="h-8 text-xs pl-8 w-44 font-body" data-testid="prospect-search" />
          </div>
          <Select value={statusFilter || "all"} onValueChange={v => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="h-8 w-36 text-xs font-body" data-testid="prospect-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={v => { setSortBy(v); setPage(1); }}>
            <SelectTrigger className="h-8 w-36 text-xs font-body"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
              <SelectItem value="next_followup">Next follow-up</SelectItem>
              <SelectItem value="last_activity">Last activity</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <Card className="bg-white border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-body" data-testid="prospects-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-3 text-left text-xs font-semibold text-slate-500">Hotel</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500 hidden md:table-cell">City</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500 hidden lg:table-cell">Email</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500">Status</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500 hidden md:table-cell">Source</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500 hidden lg:table-cell">Follow-up</th>
                <th className="p-3 text-right text-xs font-semibold text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-8 text-center"><div className="w-5 h-5 border-2 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto" /></td></tr>
              ) : prospects.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-sm text-slate-400 font-body">No prospects yet. Create one or import from CSV.</td></tr>
              ) : prospects.map(p => (
                <tr key={p.prospect_id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => { setSelectedId(p.prospect_id); setView("detail"); }} data-testid={`prospect-row-${p.prospect_id}`}>
                  <td className="p-3">
                    <p className="font-medium text-slate-800">{p.hotel_name}</p>
                    <p className="text-xs text-slate-400 md:hidden">{p.city}{p.country ? `, ${p.country}` : ""}</p>
                  </td>
                  <td className="p-3 text-slate-600 hidden md:table-cell">{p.city}{p.country ? `, ${p.country}` : ""}</td>
                  <td className="p-3 text-slate-500 hidden lg:table-cell">{p.email}</td>
                  <td className="p-3"><StateBadge status={p.status} /></td>
                  <td className="p-3 text-slate-500 hidden md:table-cell">{p.source}</td>
                  <td className="p-3 text-slate-500 hidden lg:table-cell">{p.next_followup || "—"}</td>
                  <td className="p-3 text-right" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => copyToClipboard(getTrackingUrl(p), "Link copied")} title="Copy tracking link"><Copy className="w-3 h-3" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > 50 && (
          <div className="flex justify-center gap-2 p-3 border-t border-slate-100">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <span className="text-xs text-slate-500 font-body self-center">Page {page}</span>
            <Button variant="outline" size="sm" disabled={prospects.length < 50} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        )}
      </Card>

      <CSVImportDialog open={showImport} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); loadAll(); }} />
    </div>
  );
}
